import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuthor } from '@/hooks/useAuthor';
import { useFundraiserProgress } from '@/hooks/useFundraiserProgress';
import { QuickZapButton } from '@/components/QuickZapButton';
import { Clock, Target, Zap, ArrowUpRight, Users } from 'lucide-react';
import type { Fundraiser } from '@/types/fundraiser';
import { genUserName } from '@/lib/genUserName';

interface FundraiserCardProps {
  fundraiser: Fundraiser;
  index: number;
  onZapSuccess?: () => void;
}

export function FundraiserCard({ fundraiser, index, onZapSuccess }: FundraiserCardProps) {
  const { data: author } = useAuthor(fundraiser.pubkey);
  const { data: progress, isLoading } = useFundraiserProgress(fundraiser);

  const metadata = author?.metadata;
  const displayName = metadata?.name || genUserName(fundraiser.pubkey);
  const avatarUrl = metadata?.picture;

  const goalLink = `/?fund=${fundraiser.id}`;

  const timeRemaining = fundraiser.closedAt
    ? Math.max(0, fundraiser.closedAt - Math.floor(Date.now() / 1000))
    : null;

  const daysRemaining = timeRemaining ? Math.ceil(timeRemaining / 86400) : null;
  
  const percentComplete = progress?.percentComplete || 0;
  const topSupporters = progress?.supporters?.slice(0, 3) || [];
  const goalInSats = Math.floor(fundraiser.goal / 1000);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.25, 0.4, 0.25, 1] }}
      className="group"
    >
      <Card className="h-full border-2 border-orange-500/20 bg-white hover:border-orange-500 hover:shadow-2xl hover:shadow-orange-500/20 transition-all duration-500 overflow-hidden">
        {/* Image */}
        <Link to={goalLink} className="block">
          {fundraiser.image && (
            <div className="relative w-full h-56 sm:h-64 overflow-hidden">
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
                src={fundraiser.image}
                alt={fundraiser.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              
              {/* Status Badge */}
              {progress?.isComplete && (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  className="absolute top-3 sm:top-4 right-3 sm:right-4"
                >
                  <Badge className="bg-green-500 text-white border-0 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-bold shadow-xl">
                    <Target className="h-3 sm:h-4 w-3 sm:w-4 mr-1 sm:mr-1.5" />
                    Funded
                  </Badge>
                </motion.div>
              )}
            </div>
          )}
        </Link>

        <CardContent className="p-6 sm:p-8 space-y-5 sm:space-y-6">
          {/* Author */}
          <Link to={goalLink} className="block">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <Avatar className="h-10 sm:h-12 w-10 sm:w-12 border-3 border-orange-500/30">
                <AvatarImage src={avatarUrl} alt={displayName} />
                <AvatarFallback className="bg-orange-500 text-white text-xs sm:text-sm font-bold">
                  {displayName.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-base font-bold text-black truncate">{displayName}</p>
                <p className="text-xs sm:text-sm text-orange-600 font-semibold">Creator</p>
              </div>
            </div>
          </Link>

          {/* Title */}
          <Link to={goalLink} className="block group/title">
            <h3 className="text-xl sm:text-2xl font-bold text-black line-clamp-2 group-hover/title:text-orange-600 transition-colors duration-300 leading-tight mb-2 sm:mb-3 break-words">
              {fundraiser.title}
            </h3>
            {fundraiser.summary && (
              <p className="text-sm sm:text-base text-gray-700 line-clamp-2 leading-relaxed font-medium break-words">
                {fundraiser.summary}
              </p>
            )}
          </Link>

          {/* Progress Section */}
          <div className="space-y-3 sm:space-y-4 py-3 sm:py-4">
            <div className="flex items-baseline justify-between mb-2 sm:mb-3">
              <div>
                <p className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
                  {isLoading ? '...' : (progress?.totalRaised || 0).toLocaleString()}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold mt-1">
                  of {goalInSats.toLocaleString()} sats
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl sm:text-3xl font-black text-orange-600">
                  {isLoading ? '...' : `${Math.round(percentComplete)}%`}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="relative h-3 sm:h-4 bg-orange-100 rounded-full overflow-hidden border-2 border-orange-500/30">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${percentComplete}%` }}
                transition={{ duration: 1.5, delay: index * 0.1, ease: 'easeOut' }}
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 shadow-lg"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" 
                     style={{ backgroundSize: '200% 100%' }} />
              </motion.div>
            </div>
          </div>

          {/* Top Supporters */}
          {topSupporters.length > 0 && (
            <div className="pt-3 sm:pt-4 border-t-2 border-orange-500/20">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <Zap className="h-3.5 sm:h-4 w-3.5 sm:w-4 text-orange-600" fill="currentColor" />
                <p className="text-xs sm:text-sm font-bold text-black">Top Supporters</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex -space-x-2 sm:-space-x-3">
                  {topSupporters.map((supporter) => (
                    <TopSupporterAvatar key={supporter.pubkey} pubkey={supporter.pubkey} />
                  ))}
                </div>
                <p className="text-xs sm:text-sm font-semibold text-gray-700">
                  {progress?.zapCount || 0} {progress?.zapCount === 1 ? 'supporter' : 'supporters'}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3 pt-2">
            {!progress?.isClosed && (
              <QuickZapButton 
                target={fundraiser.event}
                onZapSuccess={onZapSuccess}
                className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-bold h-12 sm:h-14 text-base sm:text-lg shadow-xl shadow-orange-500/30 hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300"
              />
            )}

            <Link to={goalLink} className="block">
              <Button 
                variant="outline" 
                className="w-full border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-bold h-11 sm:h-12 text-sm sm:text-base"
              >
                <ArrowUpRight className="h-4 sm:h-5 w-4 sm:w-5 mr-2" />
                View Details
              </Button>
            </Link>
          </div>

          {/* Time Remaining */}
          {daysRemaining !== null && !progress?.isClosed && (
            <div className="flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold text-orange-600 pt-3 sm:pt-4 border-t-2 border-orange-500/20">
              <Clock className="h-3.5 sm:h-4 w-3.5 sm:w-4" />
              <span>
                {daysRemaining === 0
                  ? 'Ends today'
                  : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left`}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function TopSupporterAvatar({ pubkey }: { pubkey: string }) {
  const { data: author } = useAuthor(pubkey);
  const metadata = author?.metadata;
  const displayName = metadata?.name || genUserName(pubkey);
  const avatarUrl = metadata?.picture;

  return (
    <Avatar className="h-8 sm:h-10 w-8 sm:w-10 border-2 sm:border-3 border-white ring-2 ring-orange-500 hover:ring-4 transition-all duration-300 hover:scale-110 cursor-pointer">
      <AvatarImage src={avatarUrl} alt={displayName} />
      <AvatarFallback className="bg-orange-500 text-white text-xs font-bold">
        {displayName.slice(0, 2).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}
